import { FC, ReactNode } from 'react'
import { PureQueryOptions } from '@apollo/client'
import gql from 'graphql-tag'
import styled from 'styled-components'
import {
  CoverLetterListItem_CurrentUserFragment,
  CoverLetterListItem_CoverLetterFragment,
} from 'generated/graphql'
import Button from '@app.components/Button'
import Loading from '@app.components/Loading/Loading'
import { LinkProps } from 'components/Link'
import CoverLetterScrap from 'screens/CoverLetterScreen/CoverLetterScrap'
import CoverLetterListItemPC from 'screens/CoverLetterScreen/desktop/components/CoverLetterList/CoverLetterListItemPC'
import BookMark from 'svgIcons/BookMark'

interface CoverLetterListItemProps {
  currentUser?: CoverLetterListItem_CurrentUserFragment | null
  coverLetter?: CoverLetterListItem_CoverLetterFragment | null
  scrapIcon?: ReactNode
  showScrapCount?: boolean
  scrapRefetchQueries?: PureQueryOptions[]
  coverLetterHighlight?: string[] | null
  LinkProps?: LinkProps
}

const CoverLetterListItemDesktop: FC<CoverLetterListItemProps> = ({
  coverLetter,
  currentUser,
  scrapIcon: initScrapIcon,
  showScrapCount = true,
  scrapRefetchQueries,
  coverLetterHighlight,
  LinkProps = {
    href: '/',
    naked: true,
  },
}) => {
  const scrapIcon = initScrapIcon ?? (
    <BookMark
      isOn={!!coverLetter?.isScrapped}
      strokeType="#666666"
      className="is-scrapped"
    />
  )

  return (
    <StyledWrapper className="root flex-wrapper">
      <div className="pc-wrapper">
        <CoverLetterListItemPC
          coverLetter={coverLetter}
          coverLetterHighlight={coverLetterHighlight}
          LinkProps={LinkProps}
        />
      </div>
      <div className="scrap">
        <CoverLetterScrap
          coverLetter={coverLetter}
          currentUser={currentUser}
          refetchQueries={scrapRefetchQueries}
        >
          {({ scrapCoverLetter, loading, scrapCount }) => (
            <>
              <Button
                onClick={scrapCoverLetter}
                className="scrap-button"
                disabled={loading}
              >
                {!loading ? scrapIcon : <Loading />}
              </Button>
              {showScrapCount && (
                <p
                  className={`content ${
                    coverLetter?.isScrapped ? 'is-scrapped' : ''
                  }`}
                >
                  {(scrapCount || 0) >= 1000 ? '999+' : scrapCount}
                </p>
              )}
            </>
          )}
        </CoverLetterScrap>
      </div>
    </StyledWrapper>
  )
}

export default CoverLetterListItemDesktop

const StyledWrapper = styled.div`
  padding: 20px 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .content {
    font-size: 16px;
    color: #999999;
    font-weight: normal;
    text-align: center;
  }

  .scrap {
    text-align: center;
    margin-right: 16px;
    width: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .is-scrapped {
    color: ${({ theme }) => theme.color.primary_2} !important;
    font-weight: 500;
  }

  .scrap-button {
    color: #e8e8e8;
    padding: 12px 12px 5px;
    background: none;

    &:hover {
      color: ${({ theme }) => theme.color.primary_2};
      background: none;
    }
  }

  .link {
    &:hover {
      text-decoration: underline;
    }
  }

  .pc-wrapper {
    width: 100%;
  }
`

gql`
  fragment CoverLetterListItem_currentUser on User {
    ...CoverLetterScrap_currentUser
  }
  fragment CoverLetterListItem_coverLetter on CoverLetter {
    id
    content
    isScrapped
    ...CoverLetterListItemMobile_coverLetter
    ...CoverLetterListItemPC_coverLetter
    ...CoverLetterScrap_coverLetter
  }
`
